import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import {
  SiteHealthReport,
  SiteHealthService,
} from 'services/SiteHealthService';

const gradeIcon: Record<SiteHealthReport['grade'], string> = {
  ok: '🟢',
  warning: '🟡',
  critical: '🔴',
};

const triggerLabel: Record<SiteHealthReport['trigger'], string> = {
  cron: '매일 아침 보고',
  manual: '전체 수동 실행',
  site: '현장 점검',
};

interface SiteHealthMemoProps {
  siteId: number;
  /** 방금 끝난 현장 점검 결과. 목록을 다시 받기 전에도 바로 보여 준다. */
  latest?: SiteHealthReport;
  busy: boolean;
  error?: string;
}

/**
 * 팀즈로 나가는 그 현장 단락을 날짜별로 읽는 메모장.
 *
 * 매일 아침 보고가 현장별로 남긴 글과, 이 화면에서 현장 점검을 눌러 만든 글이
 * 같은 자리에 하루 한 줄씩 쌓인다. 자동으로 쓴 글이라 고치지 않고 보여만 준다.
 */
const SiteHealthMemo: React.FC<SiteHealthMemoProps> = ({
  siteId,
  latest,
  busy,
  error,
}) => {
  const [rows, setRows] = useState<SiteHealthReport[]>([]);
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setListError('');
    SiteHealthService.reports(siteId, controller.signal)
      .then((items) => setRows(items))
      .catch((e) => {
        if (!controller.signal.aborted)
          setListError(
            e instanceof Error ? e.message : '보고를 불러오지 못했습니다.',
          );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
    // 점검이 끝나면(collected_at 이 바뀌면) 목록을 다시 받는다.
  }, [siteId, latest?.collected_at]);

  // 방금 만든 글이 목록보다 새 것이므로 같은 날짜 자리를 대신한다.
  const reports = useMemo(() => {
    const merged = latest
      ? [latest, ...rows.filter((r) => r.date !== latest.date)]
      : rows;
    return [...merged].sort((a, b) => b.date.localeCompare(a.date));
  }, [rows, latest]);

  const selected =
    reports.find((r) => r.date === date) || (date ? undefined : reports[0]);

  useEffect(() => setCopied(false), [selected?.date, selected?.collected_at]);

  const copy = useCallback(() => {
    if (!selected) return;
    void navigator.clipboard?.writeText(selected.markdown).then(
      () => setCopied(true),
      () => setCopied(false),
    );
  }, [selected]);

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6">현장 상태 보고</Typography>
          <Typography variant="body2" color="text.secondary">
            매일 아침 팀즈로 나가는 이 현장의 글입니다. 현장 점검을 실행하면
            지금 상태로 다시 적습니다.
          </Typography>
        </Box>
        {error && <Alert severity="error">{error}</Alert>}
        {listError && <Alert severity="warning">{listError}</Alert>}
        {busy && (
          <Box role="status">
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1 }}>
              현장 서버에 접속해 상태를 확인하고 있습니다. 몇 분 걸릴 수
              있습니다.
            </Typography>
          </Box>
        )}
        {reports.length > 0 && (
          <TextField
            select
            label="보고 날짜"
            size="small"
            value={selected?.date || ''}
            onChange={(e) => setDate(e.target.value)}
          >
            {reports.map((r) => (
              <MenuItem value={r.date} key={r.date}>
                {`${gradeIcon[r.grade] || '·'} ${r.date} · ${
                  triggerLabel[r.trigger] || r.trigger
                }`}
              </MenuItem>
            ))}
          </TextField>
        )}
        {selected && (
          <>
            <Paper
              variant="outlined"
              sx={{ p: 2, maxHeight: 420, overflow: 'auto' }}
            >
              <Typography
                component="pre"
                sx={{
                  m: 0,
                  fontFamily: 'monospace',
                  fontSize: 13,
                  lineHeight: 1.7,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {selected.markdown}
              </Typography>
            </Paper>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Button
                size="small"
                startIcon={<ContentCopy />}
                onClick={copy}
                sx={{ minHeight: 36 }}
              >
                {copied ? '복사했습니다' : '복사'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                {new Date(selected.collected_at).toLocaleString('ko-KR')} 기준
              </Typography>
            </Stack>
          </>
        )}
        {!selected && !loading && !busy && (
          <Typography color="text.secondary">
            아직 이 현장의 상태 보고가 없습니다. 현장 점검을 실행하면 팀즈로
            나가는 문구를 여기에 남깁니다.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

export default SiteHealthMemo;
